import React, { useState } from "react";
import { useApplication } from "../ApplicationProvider";
import { usePublicConfig } from "../PublicConfigProvider";
import { validateRequiredUploads, validateFullApplication } from "../../validation";
import { HEALTH_CONDITIONS, healthConditionDetail } from "../../healthConditions";
import { ReviewRow } from "../ReviewRow";
import { SponsorCopy, useSponsorPrintMode } from "../SponsorCopy";
import { mailingAddressCounty, mailingAddressOneLine } from "../../mailingAddress";

export function Step6Review({
  onJump,
  onSubmit,
  submitting = false,
}: {
  onJump: (step: number) => void;
  onSubmit: () => void;
  submitting?: boolean;
}) {
  const { data, isAmendmentMode } = useApplication();
  const cfg = usePublicConfig();

  // Round 12 — sponsor-copy print mode. While active the review content is NOT
  // rendered at all (replaced by the allow-list SponsorCopy view), so no
  // sensitive value can reach the printed page; the body class lets print CSS
  // hide the remaining page chrome (progress bar, banners).
  const [sponsorPrint, setSponsorPrint] = useState(false);
  useSponsorPrintMode(sponsorPrint, () => setSponsorPrint(false));

  const hasAsthma = data.step3.healthConditions?.asthma?.answer === "Yes";
  const hasMedications =
    data.step3.takesMedications === "Yes" &&
    data.step3.medications.some((m) => m.name.trim().length > 0);
  const uploadCheck = validateRequiredUploads({ hasAsthma, hasMedications, uploads: data.uploads });
  const fullValidation = validateFullApplication(data, { sessionStartDate: cfg.sessionStartDate });

  const TIME_LABELS: Record<string, string> = {
    morning: "Morning",
    noon: "Noon",
    evening: "Evening",
    prn: "PRN",
    other: "Other",
  };
  const medicationsSummary =
    data.step3.takesMedications === "Yes"
      ? data.step3.medications
          .filter((m) => m.name.trim())
          .map((m) => {
            const times = m.timesOfDay
              .map((t) => (t === "other" && m.timeOfDayOther.trim() ? m.timeOfDayOther.trim() : TIME_LABELS[t]))
              .join(", ");
            return `${m.name} ${m.strength}${times ? ` (${times})` : ""}`.trim();
          })
          .join("; ") || "None"
      : "None";

  const REQUIRED_UPLOADS: Array<{ key: string; label: string; step: number }> = [
    { key: "proof-of-residency", label: "Proof of US Citizenship or Legal Residency", step: 2 },
    { key: "school-certification", label: "School Certification Form", step: 2 },
    { key: "physician-statement", label: "Physician's Statement / Physical Exam", step: 3 },
    ...(hasAsthma ? [{ key: "asthma-action-plan", label: "Asthma Action Plan", step: 3 }] : []),
    ...(hasMedications
      ? [{ key: "medication-authorization", label: "Physician Medication Authorization", step: 3 }]
      : []),
    { key: "insurance-card-front", label: "Insurance card — front", step: 4 },
    { key: "insurance-card-back", label: "Insurance card — back", step: 4 },
    { key: "financial-responsibility", label: "Agreement of Financial Responsibility (signed)", step: 5 },
    { key: "liability-waiver", label: "Liability Waiver (signed)", step: 5 },
  ];

  const canSubmit = fullValidation.ok && uploadCheck.ok;

  const selfCarrySummary =
    data.step3.selfCarriesEmergencyMeds === "Yes"
      ? data.step3.selfCarryMedTypes
          .map((t) =>
            t === "Other" && data.step3.selfCarryMedOther.trim()
              ? `Other: ${data.step3.selfCarryMedOther.trim()}`
              : t
          )
          .join(", ") || "Yes"
      : data.step3.selfCarriesEmergencyMeds === "No"
        ? "None"
        : "—";

  // Health History grid summary: every Yes condition with its details, or
  // "None reported" once all rows are answered No.
  const yesConditions = HEALTH_CONDITIONS.filter(
    (c) => data.step3.healthConditions?.[c.id]?.answer === "Yes"
  );
  const allConditionsAnswered = HEALTH_CONDITIONS.every(
    (c) => data.step3.healthConditions?.[c.id]?.answer != null
  );

  const sponsorDisplay = data.sponsor.sponsorConfirmed
    ? `${data.sponsor.sponsorPostName}${data.sponsor.sponsorPostNumber ? ` #${data.sponsor.sponsorPostNumber}` : ""}`
    : "Not yet secured";

  // Round 16 — these fields physically live on data.step2 but, after the Step 1/2
  // restructure, belong to Step 1 (Family Information) and Step 3 (Emergency
  // Contact). Summary rows for them are grouped under the matching review card so
  // each Edit button lands on the step that field is now edited on.
  const parent1Display = data.step2.parent1Name.trim()
    ? `${data.step2.parent1Name} (${data.step2.parent1Relationship})` +
      `${data.step2.parent1Phone ? ` — ${data.step2.parent1Phone}` : ""}` +
      `${data.step2.parent1Email ? ` — ${data.step2.parent1Email}` : ""}`
    : "—";
  const parent2Display = data.step2.parent2Name.trim()
    ? `${data.step2.parent2Name}` +
      `${data.step2.parent2Relationship ? ` (${data.step2.parent2Relationship})` : ""}` +
      `${data.step2.parent2Phone ? ` — ${data.step2.parent2Phone}` : ""}` +
      `${data.step2.parent2Email ? ` — ${data.step2.parent2Email}` : ""}`
    : "";
  const emergencyContactDisplay = data.step2.emergencyContactName.trim()
    ? `${data.step2.emergencyContactName} (${data.step2.emergencyContactRelationship}) — ${data.step2.emergencyContactPhone}`
    : "—";

  if (sponsorPrint) {
    return <SponsorCopy onBack={() => setSponsorPrint(false)} />;
  }

  return (
    <section className="space-y-6">
      <div className="section-card flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Review &amp; submit</h2>
          <p className="text-sm text-slate-600">
            You may print this summary at any time — even before it is complete. The sponsor copy
            is meant to take to a prospective sponsor Post or Unit — it omits all personal health
            and contact information. Note: your reference number will only appear on the sponsor
            copy after you've submitted, so that may require the sponsor to contact us. The best
            approach is to submit the application, then print the sponsor copy from your
            confirmation page once the application is submitted.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button type="button" className="btn-primary" onClick={() => window.print()}>
            Print full application (for your records)
          </button>
          <button type="button" className="btn-secondary" onClick={() => setSponsorPrint(true)}>
            Print sponsor copy
          </button>
        </div>
      </div>

      <div className="section-card">
        <div className="flex justify-between items-start mb-2">
          <h2 className="text-xl font-semibold">Step 1 — Program</h2>
          <button type="button" className="btn-secondary text-xs" onClick={() => onJump(1)}>Edit</button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <ReviewRow label="Program" value={cfg.programName} />
          <ReviewRow label="Session year" value={String(data.step1.sessionYear)} />
          <ReviewRow
            label="Attestation"
            value={data.step1.attestationAcknowledged ? "✓ Acknowledged" : "✗ Not acknowledged"}
          />
          <ReviewRow label="Sponsor Post / Unit" value={sponsorDisplay} />
        </dl>
      </div>

      <div className="section-card">
        <div className="flex justify-between items-start mb-2">
          <h2 className="text-xl font-semibold">Step 1 — Basic Information</h2>
          <button type="button" className="btn-secondary text-xs" onClick={() => onJump(1)}>Edit</button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <ReviewRow label="Name" value={[data.step2.legalFirstName, data.step2.legalMiddleName, data.step2.legalLastName, data.step2.suffix].filter(Boolean).join(" ")} />
          <ReviewRow label="DOB" value={data.step2.dob || "—"} />
          <ReviewRow label="Age at program" value={data.step2.ageAtProgram != null ? String(data.step2.ageAtProgram) : "—"} />
          <ReviewRow label="Gender" value={data.step2.gender} />
          <ReviewRow label="T-shirt size" value={data.step2.tshirtSize || "—"} />
          <ReviewRow label="Student email" value={data.step2.studentEmail || "—"} />
          <ReviewRow label="Parent email" value={data.step2.parentEmail || "—"} />
          <ReviewRow label="Mailing address" value={mailingAddressOneLine(data.step2)} />
          <ReviewRow label="County" value={mailingAddressCounty(data.step2) || "—"} />
          <ReviewRow label="Cell phone" value={data.step2.cellPhone || "—"} />
          <ReviewRow label="Parent/Guardian 1" value={parent1Display} />
          {parent2Display && <ReviewRow label="Parent/Guardian 2" value={parent2Display} />}
        </dl>
      </div>

      <div className="section-card">
        <div className="flex justify-between items-start mb-2">
          <h2 className="text-xl font-semibold">Step 2 — Eligibility</h2>
          <button type="button" className="btn-secondary text-xs" onClick={() => onJump(2)}>Edit</button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <ReviewRow label="US Citizen" value={data.step2.isUSCitizen ?? "—"} />
          <ReviewRow
            label="US Legal Resident"
            value={data.step2.isUSCitizen === "No" ? data.step2.isUSLegalResident ?? "—" : "N/A"}
          />
          <ReviewRow label="School" value={`${data.step2.highSchoolName} (class of ${data.step2.graduationYear ?? "—"})`} />
          <ReviewRow label="GPA" value={String(data.step2.gpa ?? "—")} />
          <ReviewRow
            label="Eligibility confirmed"
            value={
              data.step2.eligibilityJunior &&
              data.step2.eligibilitySemesterRemaining &&
              data.step2.eligibilityNoOtherProgram &&
              data.step2.eligibilityFullSession
                ? "✓ All criteria confirmed"
                : "✗ Incomplete"
            }
          />
        </dl>
      </div>

      <div className="section-card">
        <div className="flex justify-between items-start mb-2">
          <h2 className="text-xl font-semibold">Step 3 — Health History</h2>
          <button type="button" className="btn-secondary text-xs" onClick={() => onJump(3)}>Edit</button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <ReviewRow label="Emergency contact" value={emergencyContactDisplay} />
          <ReviewRow label="PCP" value={`${data.step3.pcpName} — ${data.step3.pcpPhone}`} />
          <ReviewRow label="Last physical" value={data.step3.lastPhysicalDate || "—"} />
          <ReviewRow label="Medications" value={medicationsSummary} />
          <ReviewRow label="Self-carried emergency medication" value={selfCarrySummary} />
          <ReviewRow
            label="Allergies"
            value={
              data.step3.hasAllergies === "Yes"
                ? data.step3.allergies
                    .filter((a) => a.allergy)
                    .map(
                      (a) =>
                        `${a.allergy}${a.category ? ` (${a.category})` : ""} → ${a.reaction}`
                    )
                    .join("; ") || "None"
                : "None"
            }
          />
          <ReviewRow label="Vaccines up to date?" value={data.step3.vaccinesUpToDate ?? "—"} />
          <ReviewRow label="Accommodation request" value={data.step3.accommodationRequest ?? "—"} />
          <ReviewRow label="Health changes since physical" value={data.step3.healthChangesSincePhysical ?? "—"} />
        </dl>
        <div className="mt-3 border-t border-slate-100 pt-3 text-sm">
          <p className="text-slate-500 mb-1">Health history conditions</p>
          {yesConditions.length > 0 ? (
            <ul className="list-disc list-inside space-y-1">
              {yesConditions.map((c) => {
                const detail = healthConditionDetail(c, data.step3.healthConditions[c.id]);
                return (
                  <li key={c.id} className="text-slate-900">
                    <span className="font-medium">{c.label}</span>
                    {detail && <> — {detail}</>}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-slate-900">
              {allConditionsAnswered ? "None reported" : "Incomplete — answer every condition in Step 3"}
            </p>
          )}
        </div>
      </div>

      <div className="section-card">
        <div className="flex justify-between items-start mb-2">
          <h2 className="text-xl font-semibold">Step 4 — Health Insurance</h2>
          <button type="button" className="btn-secondary text-xs" onClick={() => onJump(4)}>Edit</button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <ReviewRow label="Carrier" value={data.step4.carrierName || "—"} />
          <ReviewRow label="Policy / member ID" value={data.step4.policyId || "—"} />
          <ReviewRow label="Group number" value={data.step4.groupNumber || "—"} />
          <ReviewRow label="Policyholder" value={data.step4.policyholderName || "—"} />
          <ReviewRow label="Claims phone" value={data.step4.claimsPhone || "—"} />
        </dl>
      </div>

      <div className="section-card">
        <div className="flex justify-between items-start mb-2">
          <h2 className="text-xl font-semibold">Step 5 — Certifications</h2>
          <button type="button" className="btn-secondary text-xs" onClick={() => onJump(5)}>Edit</button>
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <ReviewRow label="Financial responsibility" value={data.step5.financialResponsibilityAck ? "✓ Acknowledged" : "✗ Missing"} />
          <ReviewRow label="Liability waiver" value={data.step5.liabilityWaiverAck ? "✓ Acknowledged" : "✗ Missing"} />
          <ReviewRow label="Photo / media release" value={data.step5.photoReleaseAck ? "✓ Acknowledged" : "✗ Missing"} />
          <ReviewRow label="Code of conduct" value={data.step5.codeOfConductAck ? "✓ Acknowledged" : "✗ Missing"} />
          <ReviewRow label="Medical treatment authorization" value={data.step5.medicalTreatmentAck ? "✓ Acknowledged" : "✗ Missing"} />
          <ReviewRow label="Applicant Certification" value={data.step5.applicantCertificationAck ? "✓ Acknowledged" : "✗ Missing"} />
          <ReviewRow label="Parent/Guardian Certification" value={data.step5.parentCertificationAck ? "✓ Acknowledged" : "✗ Missing"} />
          <ReviewRow label="Final attestation" value={data.step5.finalAttestation ? "✓ Confirmed" : "✗ Missing"} />
        </dl>
      </div>

      <div className="section-card">
        <h2 className="text-xl font-semibold mb-3">Required uploads</h2>
        <ul className="space-y-1 text-sm">
          {REQUIRED_UPLOADS.map((u) => {
            const present = !!data.uploads[u.key];
            return (
              <li key={u.key} className="flex items-center gap-2">
                <span className={present ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                  {present ? "✓" : "✗"}
                </span>
                <span className="flex-1">{u.label}</span>
                {!present && (
                  <button type="button" className="text-xs underline text-program" onClick={() => onJump(u.step)}>
                    Go to step {u.step}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {!canSubmit && (
        <div className="no-print rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900">
          <p className="font-medium mb-1">Some required information is missing or invalid:</p>
          <ul className="list-disc list-inside space-y-1">
            {fullValidation.errors.slice(0, 12).map((e, i) => (
              <li key={i}>
                Step {e.step}: {e.message} {e.path ? <span className="opacity-70">({e.path})</span> : null}
              </li>
            ))}
            {fullValidation.errors.length > 12 && (
              <li>… and {fullValidation.errors.length - 12} more.</li>
            )}
          </ul>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          className="btn-primary"
          disabled={!canSubmit || submitting}
          onClick={onSubmit}
        >
          {submitting ? "Submitting…" : isAmendmentMode ? "Resubmit" : "Submit application"}
        </button>
      </div>
    </section>
  );
}
